import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import ScopeField from '../../components/ScopeField';
import { fetchBankReport } from '../../api/bankReport';
import { downloadCSV } from '../../utils/csv';
import { generateReportPdf } from '../../utils/pdf';
import { useAuth } from '../../context/AuthContext';
import './BankReportPage.css';

const ORDER_FIELD_OPTIONS = ['Code', 'Nom'];
const DEFAULT_ORDER = [
  { field: 'Code', dir: 'Asc' },
  { field: 'Nom', dir: 'Asc' },
];
const QTY_OPS = ['>', '>=', '<', '<=', '='];

const filterApiValue = (filter) => {
  if (filter === 'Groupe') return 'Group';
  if (filter === 'Superviseur') return 'Supervisor';
  if (filter === 'Banque') return 'Bank';
  return 'All';
};

const BankReportPage = () => {
  const { company } = useAuth();
  const [filter, setFilter] = useState('Tout');
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');
  const [order, setOrder] = useState(DEFAULT_ORDER);
  const [withDetails, setWithDetails] = useState(true);
  const [qtyOp, setQtyOp] = useState('>');
  const [qtyValue, setQtyValue] = useState('0');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [blocks, setBlocks] = useState(null);

  const updateOrder = (i, field, value) => {
    setOrder((prev) => prev.map((o, idx) => (idx === i ? { ...o, [field]: value } : o)));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await fetchBankReport({
        filter: filterApiValue(filter),
        scope: filter === 'Tout' ? undefined : scopeId,
        orderBy: order,
        withDetails,
        qty: { op: qtyOp, value: qtyValue },
      });
      setBlocks(data);
    } catch (err) {
      setError('Recherche impossible.');
      setBlocks(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCsv = () => {
    if (!blocks || blocks.length === 0) return;
    const rows = [['Banques Rapport'], []];
    blocks.forEach((block) => {
      rows.push(['ID', 'Banque', 'Adresse']);
      rows.push([block.seq, block.name, block.address]);
      if (block.terminals?.length) {
        rows.push(['ID', 'Terminal', 'Nom', 'Adresse', 'Téléphone', 'Statut']);
        block.terminals.forEach((t) => rows.push([t.id ?? '', t.terminal, t.nom, t.adresse || '', t.telephone || '', t.actif === false ? 'Inactif' : 'Actif']));
      }
      rows.push([]);
    });
    downloadCSV('banques-rapport.csv', rows);
  };

  const handleDownloadPdf = async () => {
    if (!blocks || blocks.length === 0) return;
    const sections = blocks.map((block) => ({
      heading: `${block.name}${block.address ? ' — ' + block.address : ''}`, // nom = "group1-1" (N° inclus)
      columns: ['ID', 'Terminal', 'Nom', 'Adresse', 'Téléphone', 'Statut'],
      rows: (block.terminals || []).map((t) => [t.id ?? '', t.terminal, t.nom, t.adresse || '', t.telephone || '', t.actif === false ? 'Inactif' : 'Actif']),
    }));
    await generateReportPdf({
      company,
      title: 'Banques Rapport',
      sections,
      filename: 'banques-rapport.pdf',
    });
  };

  return (
    <Layout>
      <p className="br-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/rapports" className="bc-link">Reports</Link>
        {' >> '}
        <span className="bc-current">Banques Rapport</span>
      </p>

      <div className="br-card">
        <div className="br-card-header">
          <span className="br-card-title">Banques Rapport</span>
        </div>
        <div className="br-card-body">
          <div className="br-radio-row">
            {['Tout', 'Groupe', 'Superviseur', 'Banque'].map((opt) => (
              <label key={opt} className="br-radio-label">
                <input
                  type="radio"
                  name="filter"
                  value={opt}
                  checked={filter === opt}
                  onChange={() => { setFilter(opt); setScopeId(''); setScopeName(''); }}
                />
                {opt}
              </label>
            ))}
          </div>

          {filter !== 'Tout' && (
            <div className="br-row br-scope-row">
              <label className="br-label">{filter}</label>
              <ScopeField
                filter={filter}
                id={scopeId}
                name={scopeName}
                onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); }}
                inputClass="br-input br-input-sm"
                iconClass="br-icon-btn"
                nameClass="br-copy-name"
              />
            </div>
          )}

          <form onSubmit={handleSearch}>
            <p className="br-section-title">Commande par</p>
            {order.map((o, i) => (
              <div key={i} className="br-order-row">
                <select
                  className="br-select br-select-field"
                  value={o.field}
                  onChange={e => updateOrder(i, 'field', e.target.value)}
                >
                  {ORDER_FIELD_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
                <select
                  className="br-select br-select-dir"
                  value={o.dir}
                  onChange={e => updateOrder(i, 'dir', e.target.value)}
                >
                  <option value="Asc">Asc</option>
                  <option value="Desc">Desc</option>
                </select>
              </div>
            ))}

            <div className="br-row">
              <label className="br-label">Avec des Details</label>
              <input type="checkbox" checked={withDetails} onChange={e => setWithDetails(e.target.checked)} />
            </div>

            <div className="br-row">
              <label className="br-label">Qte Terminal</label>
              <select className="br-select br-qty-op" value={qtyOp} onChange={e => setQtyOp(e.target.value)}>
                {QTY_OPS.map(op => <option key={op} value={op}>{op}</option>)}
              </select>
              <input
                className="br-input br-qty-value"
                type="number"
                value={qtyValue}
                onChange={e => setQtyValue(e.target.value)}
              />
            </div>

            {error && <p className="br-error">{error}</p>}

            <button type="submit" className="br-search-btn" disabled={loading}>
              {loading ? 'Recherche...' : 'Chercher'}
            </button>
          </form>
        </div>
      </div>

      {blocks && (
        <div className="br-card br-results">
          <div className="br-card-header br-results-header">
            <span className="br-card-title">Banques Rapport</span>
            {blocks.length > 0 && (
              <div className="br-export-btns">
                <button type="button" className="br-export-btn" onClick={handleDownloadCsv}>Télécharger</button>
                <button type="button" className="br-export-btn" onClick={handleDownloadPdf}>PDF</button>
              </div>
            )}
          </div>
          <div className="br-card-body">
            {blocks.length === 0 ? (
              <p className="br-empty">Aucun group sur cette portée.</p>
            ) : (
              blocks.map((block) => (
                <div key={block.id ?? 'sans-groupe'} className="br-block">
                  <table className="br-block-table">
                    <thead>
                      <tr><th>ID</th><th>Banque</th><th>Adresse</th></tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{block.seq}</td>
                        <td>{block.name}</td>
                        <td>{block.address || '.'}</td>
                      </tr>
                    </tbody>
                  </table>
                  {block.terminals && (
                    <table className="br-terminal-table">
                      <thead>
                        <tr><th>ID</th><th>Terminal</th><th>Nom</th><th>Adresse</th><th>Téléphone</th><th>Statut</th></tr>
                      </thead>
                      <tbody>
                        {block.terminals.map((t, i) => (
                          <tr key={i}>
<td>{t.id ?? ''}</td>
                            <td>{t.terminal || '—'}</td>
                            <td>{t.nom}</td>
                            <td>{t.adresse || '.'}</td>
                            <td>{t.telephone || '.'}</td>
                            <td>{t.actif === false ? 'Inactif' : 'Actif'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default BankReportPage;
